import { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowLayout,
  StyledContainer,
  StyledTitle,
  IndicatorWrapper,
  Indicator,
} from '@/components/Approval/CategoryContent/styles';
import {
  FavoriteChecked,
  FavoriteDefault,
} from '@/theme/overrides/CustomIcons';
import TooltipBox from '@/components/Approval/CategoryContent/TooltipBox';
import DocumentTooltipBox from '@/components/Approval/Content/DocumentFilters/TooltipBox';
import { ApprovalFilterType } from '@/types/api';
import AnimationOpen from '@/components/MobileSales/Contents/AnimationOpen';
import ArrowIcon from '@/public/icons/openArrow.svg';

type IdType = ApprovalFilterType | string;

type P<T> = {
  title: string;
  id: T;
  count?: number;
  children?: ReactNode;
  isChildren?: boolean;
  onClick(id: T): void;
  selectedId?: IdType;
  favoriteId?: string;
  isDocumentFilter?: boolean;
  /** 미확인 신규 상태변경(완료/반려) 표시용 빨간 점 뱃지. */
  showDot?: boolean;
};

const CategoryContent = <T extends IdType>({
  id,
  title,
  count,
  children,
  onClick,
  isChildren = false,
  selectedId,
  favoriteId,
  isDocumentFilter = false,
  showDot = false,
}: P<T>) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);
  const selectedItem = id === selectedId;
  const isBookMark = id === 0;

  // "내 필터" 카테고리일 때, 자식 필터가 선택되어 있으면 열림 상태 유지
  useEffect(() => {
    if (
      id === ApprovalFilterType['내 필터'] &&
      typeof selectedId === 'string'
    ) {
      setOpen(true);
    }
  }, [id, selectedId]);

  const handleClick = () => {
    onClick(id);
    setOpen(!open);
  };

  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const [isHovered, setIsHovered] = useState(false);

  const handleHoverClose = () => {
    setIsHovered(false);
  };

  return (
    <>
      <StyledContainer
        selectedItem={selectedItem}
        isChildren={isChildren}
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className="category-content-container"
      >
        <StyledTitle>
          {isChildren && (
            <IndicatorWrapper>
              <Indicator selectedItem={selectedItem} />
            </IndicatorWrapper>
          )}
          <div
            style={{
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
            }}
          >
            {title}
            {showDot && (
              <span
                aria-label={t('새 문서 있음')}
                style={{
                  display: 'inline-block',
                  width: '5px',
                  height: '5px',
                  marginLeft: '2px',
                  borderRadius: '50%',
                  backgroundColor: '#F55060',
                  verticalAlign: 'top',
                }}
              />
            )}
            <span
              style={{
                marginLeft: '8px',
                fontWeight: selectedItem ? '700' : '500',
              }}
            >
              {count?.toLocaleString()}
            </span>
          </div>
        </StyledTitle>
        {/*FIXME: Hydration*/}
        {isBookMark ? (
          selectedItem ? (
            <FavoriteChecked />
          ) : (
            <FavoriteDefault />
          )
        ) : null}
        {isClient && children && (
          <ArrowLayout open={open} selectedItem={selectedItem}>
            <ArrowIcon />
          </ArrowLayout>
        )}
        {favoriteId &&
          (isDocumentFilter ? (
            <DocumentTooltipBox
              modifyItem={{ title, id: favoriteId }}
              isHovered={isHovered}
              handleHoverClose={handleHoverClose}
            />
          ) : (
            <TooltipBox
              modifyItem={{ title, id: favoriteId }}
              isHovered={isHovered}
              handleHoverClose={handleHoverClose}
            />
          ))}
      </StyledContainer>
      {open && <AnimationOpen>{children}</AnimationOpen>}
    </>
  );
};
export default CategoryContent;
